import React, { ReactNode } from "react";
import { motion } from "framer-motion";
import type { OnboardingAnswers, Placement } from "../../types/onboarding";
import { goalOptions, minuteOptions, reminderOptions, skillOptions } from "../../data/onboarding";
import { PairChips } from "../ui/LangChip";
import { LevelMeter } from "./LevelMeter";
import { courseTitle } from "../../utils/language";
interface PlanSoFarProps {
  answers: OnboardingAnswers;
  placement: Placement;
}

/** Live side panel on desktop: the plan visibly assembles itself as the learner answers. */
export function PlanSoFar({
  answers,
  placement
}: PlanSoFarProps) {
  const minutes = minuteOptions.find(m => m.minutes === answers.minutes);
  const reminder = reminderOptions.find(r => r.id === answers.reminder);
  return <aside aria-label="Your plan so far" className="rounded-panel border-3 border-line bg-surface p-5 shadow-brutal-sm" data-id="element-909" data-mp="3esxuvr5ori2y">
      <h2 className="text-sm font-bold text-ink-2" data-id="element-910" data-mp="39tl5ozrcdyx7">Your plan so far</h2>
      <dl className="mt-4 divide-y divide-line/10" data-id="element-911" data-mp="2dzlfr2h8bp4l">
        <Row label="Course" filled={Boolean(answers.target)} data-id="element-912" data-mp="2dysc4adkhfqo">
          <span className="flex flex-col gap-2" data-id="element-913" data-mp="2i1orls7t8vqe">
            {answers.targets.map((code, i) => <span key={code} className="flex items-center gap-2.5" data-id="element-914" data-mp="241xgq0clbvgm" data-mpi={i}>
                <PairChips target={code} base={answers.base} size="sm" data-id="element-915" data-mp="243qrnru8l1uf" />
                <span className="text-sm font-semibold text-ink" data-id="element-916" data-mp="290qxc1n6c2l0">
                  {courseTitle({
                target: code,
                base: answers.base
              })}
                  {i === 0 && answers.targets.length > 1 && <span className="ml-1.5 text-xs font-bold text-ink-2" data-id="element-917" data-mp="31nuujkg0zcm8">Main</span>}
                </span>
              </span>)}
          </span>
        </Row>
        <Row label="Why" filled={answers.goals.length > 0} data-id="element-918" data-mp="2rytg3yp4p3ad">
          <span className="text-sm font-semibold text-ink" data-id="element-919" data-mp="2rx9zu83h2s8l">
            {answers.goals.map(g => goalOptions.find(o => o.id === g)?.label).join(", ")}
          </span>
        </Row>
        <Row label="Starting point" filled={answers.selfLevel !== undefined} data-id="element-920" data-mp="3kh8hp3dhyera">
          <span className="block text-sm font-semibold text-ink" data-id="element-921" data-mp="3ual5fvo64fmc">
            {placement.label} · Unit {placement.unit}
          </span>
          <span className="mt-2 block" data-id="element-922" data-mp="3paglfvo1x2rd">
            <LevelMeter band={placement.band} compact data-id="element-923" data-mp="3kcx6sunfynyw" />
          </span>
        </Row>
        <Row label="Focus" filled={answers.focus.length > 0} data-id="element-924" data-mp="3gc4yc3me8n3o">
          <span className="text-sm font-semibold text-ink" data-id="element-925" data-mp="3gblicsgb08sa">
            {answers.focus.map(f => skillOptions.find(s => s.key === f)?.label).join(", ")}
          </span>
        </Row>
        <Row label="Daily" filled={Boolean(minutes)} data-id="element-926" data-mp="36kh3dsqmonxg">
          <span className="text-sm font-semibold text-ink" data-id="element-927" data-mp="31mfjj5ldokkx">
            {answers.minutes} min{reminder && reminder.id !== "none" ? ` · reminder at ${reminder.time}` : ""}
          </span>
        </Row>
      </dl>
    </aside>;
}
function Row({
  label,
  filled,
  children
}: {
  label: string;
  filled: boolean;
  children: ReactNode;
}) {
  return <div className="py-3 first:pt-0 last:pb-0" data-id="element-928" data-mp="2nnpnltaaoqy5">
      <dt className="text-xs font-semibold text-ink-2" data-id="element-929" data-mp="135f2vw95isuc">{label}</dt>
      <dd className="mt-1 min-h-[22px]" data-id="element-930" data-mp="1379egjyj98lr">
        {filled ? <motion.div key="v" initial={{
        opacity: 0,
        y: 4
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.18
      }} data-id="element-931" data-mp="1836x4wx9qr1p">
            {children}
          </motion.div> : <span className="block h-[18px] w-24 rounded-md bg-sunken" data-id="element-932" data-mp="1h56c7joq3l20" />}
      </dd>
    </div>;
}